import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { LogEvents } from '../../constants/log_events.constants';
import { UserLocaleContext } from '../../contexts/user_locale/user_locale.context';
import { getParticipantDetails } from '../../helpers/participants.helpers';
import { getActivityTaskResponses, getPIOversightList, getPIOversightReviewBundle, getPIOversightSummary, prepareReviewBundle, } from '../../helpers/pi_oversight.helpers';
import { isChinaCluster } from '../../helpers/study.helpers';
import { getParticipantVisitScheduleData } from '../../helpers/visit.helpers';
import { PIOversightStatus, } from '../../types/pi_oversight.types';
import { useCurrentEnvironment } from '../use_current_environment';
import { useLumosEventLogger } from '../use_lumos_event_logger';
import { useOrgContext } from '../use_org_context';
import { useSiteContext } from '../use_site_context';
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 0;
const SEARCH_DEBOUNCE_MS = 300;
const NO_PARTICIPANT_NUMBER_PLACEHOLDER = 'Participant number has not been created yet for the participant.';
/**
 * Custom hook for managing PI Oversight data and operations
 *
 * This hook provides comprehensive functionality for:
 * - Fetching PI oversight summary data
 * - Fetching DCR (Data Change Request) items
 * - Fetching task responses
 *
 * @param participantId - The ID of the participant for PI oversight
 * @returns Object containing state and action functions
 */
export const usePIOversight = ({ participantId, } = {}) => {
    const webApi = useApiClient(ApiClientService.WEB);
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const org = useOrgContext();
    const { logLumosEvent } = useLumosEventLogger();
    const translate = useLocalizeMessage();
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const siteUserLocale = getUserSelectedLocale();
    const searchTimeoutRef = useRef();
    const [pagination, setPagination] = useState({
        pageIndex: DEFAULT_PAGE_INDEX,
        pageSize: DEFAULT_PAGE_SIZE,
    });
    const [piOversightStatus, setPIOversightStatus] = useState({
        label: translate('label.pi_oversight.status.overdue'),
        value: PIOversightStatus.OVERDUE,
    });
    const [searchTerm, setSearchTerm] = useState({
        value: '',
        debouncedValue: '',
    });
    const isChinaEnv = environment ? isChinaCluster(environment) : false;
    const logEvent = (event, payload = {}) => {
        const payloadToSend = {
            participantId,
            ...payload,
        };
        logLumosEvent(event, payloadToSend);
    };
    const getParticipantData = async () => {
        if (!environment || !site) {
            return {};
        }
        return await getParticipantDetails({
            environment,
            siteId: site?._id ?? '',
            participantId: participantId ?? '',
        });
    };
    const getVisitScheduleData = async () => {
        if (!environment || !site || !participantId) {
            return [];
        }
        try {
            const response = await getParticipantVisitScheduleData({
                environment,
                siteId: site._id,
                participantId,
                locale: siteUserLocale,
            });
            return response?.data?.data || [];
        }
        catch (error) {
            logEvent(LogEvents.PI_OVERSIGHT_VISIT_SCHEDULE_FETCH_FAILED, {
                error: error instanceof Error ? error.message : String(error),
            });
            return [];
        }
    };
    const getPIOversightListData = async () => {
        if (!environment || !site || !org) {
            return {};
        }
        const params = {
            limit: pagination.pageSize,
            skip: pagination.pageIndex * pagination.pageSize,
        };
        if (searchTerm.debouncedValue) {
            params['participantNumber'] = searchTerm.debouncedValue;
        }
        params['status'] = piOversightStatus.value;
        return await getPIOversightList({
            environment,
            params: {
                siteId: site?._id ?? '',
                studyId: org?._id ?? '',
                ...params,
            },
        });
    };
    const getReviewBundle = async () => {
        if (!environment || !site || !org) {
            return {};
        }
        return await getPIOversightReviewBundle({
            environment,
            params: {
                siteId: site._id,
                studyId: org._id,
                participantId: participantId ?? '',
            },
            onError: (error) => {
                logEvent(LogEvents.PI_OVERSIGHT_REVIEW_BUNDLE_FETCH_FAILED, {
                    error: error?.message,
                });
            },
        });
    };
    const generatePIOversightSummary = async () => {
        if (!environment || !site || !org || !reviewBundleData?.data || isChinaEnv) {
            if (isChinaEnv) {
                logEvent(LogEvents.PI_OVERSIGHT_SUMMARY_FETCH_FAILED_IN_CHINA_ENV, {
                    error: 'China environment does not support PI oversight summary.',
                });
            }
            return {};
        }
        const reviewBundle = reviewBundleData?.data || {};
        const nonPersonalResponses = prepareReviewBundle(reviewBundle);
        const dcrItems = reviewBundle.dcrItems ?? [];
        const visitEvents = reviewBundle.visitEvents ?? [];
        if (dcrItems.length === 0 && nonPersonalResponses.length === 0 && visitEvents.length === 0) {
            return {
                cached: false,
                generatedAt: new Date().toISOString(),
                object: 'pi_oversight_summary',
                summary: translate('info.pi_oversight.no_recent_data'),
            };
        }
        const visitSchedule = visitScheduleData || [];
        const payload = {
            siteId: site._id,
            siteName: site.c_name,
            studyId: org._id,
            studyName: environment.study?.name || '',
            participantId: reviewBundle.participantId,
            participantNumber: reviewBundle.participantNumber || NO_PARTICIPANT_NUMBER_PLACEHOLDER,
            lastSignedAtUtc: reviewBundle.lastSignedAtUtc,
            responses: nonPersonalResponses,
            serverHash: reviewBundle.serverHash,
            dcrItems,
            visitEvents,
            visitSchedule,
        };
        const response = await getPIOversightSummary({
            webApi,
            payload,
            onError: (error) => {
                logEvent(LogEvents.PI_OVERSIGHT_SUMMARY_FETCH_FAILED, {
                    error: error?.message,
                });
            },
        });
        return response;
    };
    const handlePaginationChange = (paginationState) => {
        setPagination(paginationState);
    };
    const handleSearchChange = (value) => {
        setSearchTerm((p) => ({ ...p, value: value }));
    };
    const handleSearchClear = () => {
        setSearchTerm((p) => ({ ...p, value: '' }));
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    const handleSelectPIOversightStatus = (selected) => {
        setPIOversightStatus(selected);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    const { data: participantData, ...fetchParticipantRequestState } = useQuery({
        queryKey: ['participantDetails', participantId],
        queryFn: getParticipantData,
        enabled: !!environment && !!site?._id && !!participantId,
    });
    const { data: piOversightData, ...fetchPIOversightListRequestState } = useQuery({
        queryKey: [
            'piOversightList',
            piOversightStatus.value,
            searchTerm.debouncedValue,
            pagination.pageIndex,
            pagination.pageSize,
            site?._id,
            org?._id,
        ],
        queryFn: getPIOversightListData,
        enabled: !!environment && !!site?._id && !!org?._id && !participantId,
    });
    const { data: reviewBundleData, ...fetchReviewBundleRequestState } = useQuery({
        queryKey: ['reviewBundleData', participantId, site?._id, org?._id],
        queryFn: getReviewBundle,
        enabled: !!environment && !!participantId && !!site?._id && !!org?._id,
    });
    // Cache is invalidated in visit_confirmation.component.tsx and participant_visits.component.tsx
    // when visits are started/skipped to ensure fresh visit schedule data
    const { data: visitScheduleData } = useQuery({
        queryKey: ['visitScheduleData', participantId, site?._id],
        queryFn: getVisitScheduleData,
        enabled: !!environment && !!participantId && !!site?._id,
    });
    const visitScheduleKey = visitScheduleData
        ?.map((visit) => [
        visit.c_visit,
        visit.c_window_start_date ?? String(visit.c_window_start ?? ''),
        visit.c_window_end_date ?? String(visit.c_window_end ?? ''),
    ].join(':'))
        .join('|');
    // Summary query waits for fresh review bundle data by ensuring:
    // 1. Data exists with serverHash
    // 2. Review bundle is NOT currently fetching (prevents using stale cache)
    // 3. Either: query fetched successfully, OR data exists from cache/setQueryData (for tests)
    // Note: The serverHash in the query key automatically triggers refetch when it changes
    const reviewBundleReady = !!reviewBundleData?.data?.serverHash &&
        !fetchReviewBundleRequestState.isFetching &&
        (fetchReviewBundleRequestState.isSuccess || fetchReviewBundleRequestState.isFetched);
    const { data: summaryData, ...fetchSummaryRequestState } = useQuery({
        queryKey: ['summaryData', participantId, site?._id, org?._id, visitScheduleKey, reviewBundleData?.data?.serverHash],
        queryFn: generatePIOversightSummary,
        enabled: reviewBundleReady && !!environment && (!!visitScheduleData || !participantId),
    });
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setSearchTerm((p) => ({ ...p, debouncedValue: p.value }));
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = undefined;
            }
        };
    }, [searchTerm.value]);
    return {
        participantData: participantData ?? {},
        piOversightData: piOversightData ?? {},
        reviewBundle: reviewBundleData?.data ?? {},
        summary: summaryData ?? {},
        fetchParticipantRequestState,
        fetchPIOversightListRequestState,
        fetchReviewBundleRequestState,
        fetchSummaryRequestState: {
            ...fetchSummaryRequestState,
            // For China environments, mark the query as fetched/success since we're not calling the endpoint
            isPending: isChinaEnv ? false : fetchSummaryRequestState.isPending,
            isFetched: isChinaEnv ? true : fetchSummaryRequestState.isFetched,
        },
        getActivityTaskResponses,
        handlePaginationChange,
        handleSearchChange,
        handleSearchClear,
        handleSelectPIOversightStatus,
        pagination,
        piOversightStatus,
        searchTerm,
    };
};
